// src/screens/OAuthCallback.js
import { useEffect, useState } from 'react';
import { useNavigate }         from 'react-router-dom';
import { useAuth }             from '../contexts/AuthContext';
import axios                   from 'axios';

const API_BASE = 'https://revexia.ai/api';

const parseJwt = (token) => {
  try { return JSON.parse(atob(token.split('.')[1])); }
  catch { return null; }
};

const OAuthCallback = () => {
  const navigate         = useNavigate();
  const { login }        = useAuth();
  const [status, setStatus] = useState('loading'); // 'loading' | 'error'
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const accessToken  = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const token        = params.get('token');
    const oauthEmail   = params.get('email') || '';
    const oauthError   = params.get('error');
    const code         = params.get('code');

    const isGoogle   = window.location.pathname.includes('google');
    const isFacebook = window.location.pathname.includes('facebook');
    const provider   = isFacebook ? 'facebook' : 'google';

    // ── Don't clean URL yet — we need search params for the API call ──────
    if (oauthError) {
      window.history.replaceState({}, document.title, window.location.pathname);
      setStatus('error');
      setErrMsg(decodeURIComponent(oauthError));
      setTimeout(() => navigate('/auth'), 3000);
      return;
    }

    // ── Scenario A: tokens already in URL (after backend fix) ─────────────
    const finalToken   = accessToken || token;
    const finalRefresh = refreshToken || '';

    if (finalToken) {
      window.history.replaceState({}, document.title, window.location.pathname);
      saveAndLogin(finalToken, finalRefresh, oauthEmail);
      return;
    }

    // ── Scenario B: raw ?code= forwarded to frontend ──────────────────────
    if (code) {
      // Forward full query string back to backend to exchange for token
      const callbackUrl = `${API_BASE}/auth/${provider}/callback${window.location.search}`;

      // Clean URL before async call
      window.history.replaceState({}, document.title, window.location.pathname);

      axios.get(callbackUrl)
        .then(({ data }) => {
          const at = data.access_token;
          const rt = data.refresh_token || '';
          if (!at) throw new Error('No access token returned from server.');
          const emailFromData = data.email || '';
          saveAndLogin(at, rt, emailFromData);
        })
        .catch((err) => {
          const msg =
            err.response?.data?.detail ||
            err.response?.data?.message ||
            (typeof err.response?.data === 'string' ? err.response.data : null) ||
            err.message ||
            'Sign-in failed. Please try again.';
          setStatus('error');
          setErrMsg(msg);
          setTimeout(() => navigate('/auth'), 3000);
        });

      return;
    }

    // ── Nothing in URL ─────────────────────────────────────────────────────
    window.history.replaceState({}, document.title, window.location.pathname);
    setStatus('error');
    setErrMsg('No authentication data found.');
    setTimeout(() => navigate('/auth'), 3000);
  }, []);

  const saveAndLogin = (at, rt, emailHint) => {
    localStorage.setItem('access_token', at);
    if (rt) localStorage.setItem('refresh_token', rt);

    const decoded = parseJwt(at);
    const email   = emailHint || decoded?.sub || decoded?.email || '';
    const name    = email.split('@')[0] || 'User';

    login({ email, name }, at);
    navigate('/dashboard', { replace: true });
  };

  // ── UI ────────────────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>

      {status === 'error' ? (
        <>
          <div style={styles.errorCircle}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#f87171" strokeWidth="2" />
              <path d="M12 8v4M12 16h.01" stroke="#f87171" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </div>
          <p style={styles.errorTitle}>Sign-in Failed</p>
          <p style={styles.errorMsg}>{errMsg}</p>
          <p style={styles.subText}>Redirecting to login…</p>
        </>
      ) : (
        <>
          <div style={styles.spinner} />
          <p style={styles.mainText}>Completing sign-in…</p>
          <p style={styles.subText}>Please wait while we verify your account</p>
        </>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: '#000000',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '14px',
    fontFamily: 'Inter, sans-serif',
    padding: '20px',
  },
  spinner: {
    width: '44px',
    height: '44px',
    border: '3px solid rgba(91,127,255,0.15)',
    borderTop: '3px solid #5B7FFF',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  mainText: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '500',
    margin: 0,
  },
  subText: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: '13px',
    margin: 0,
  },
  errorCircle: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    background: 'rgba(239,68,68,0.08)',
    border: '2px solid rgba(239,68,68,0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorTitle: {
    color: '#f87171',
    fontSize: '16px',
    fontWeight: '600',
    margin: 0,
  },
  errorMsg: {
    color: 'rgba(248,113,113,0.8)',
    fontSize: '13px',
    margin: 0,
    textAlign: 'center',
    maxWidth: '320px',
    lineHeight: '1.5',
  },
};

export default OAuthCallback;
